import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Browse All Categories');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            Explore Product Categories
          </h2>
          <p className="text-foreground-600 max-w-xl mx-auto text-sm md:text-base">
            Browse our complete catalog of premium products. Select a category to start shopping on the main website.
          </p>
        </div>

        {/* Dropdown Selector */}
        <div className="max-w-md mx-auto mb-12 relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border border-background-200/80 rounded-xl text-foreground-700 hover:border-primary-300 transition-all duration-200 cursor-pointer"
          >
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center text-primary-500">
                <i className="ri-grid-line text-base"></i>
              </div>
              <span className="text-sm font-medium">{selectedCategory}</span>
            </div>
            <div className="w-5 h-5 flex items-center justify-center text-foreground-400">
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-sm"></i>
              ) : (
                <i className="ri-arrow-down-s-line text-sm"></i>
              )}
            </div>
          </button>

          {dropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200/80 rounded-xl py-2 max-h-72 overflow-y-auto z-20">
              {categories.map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategorySelect(cat)}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors text-left cursor-pointer"
                >
                  <div className="w-12 h-10 rounded-md overflow-hidden flex-shrink-0">
                    <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover object-top" loading="lazy" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{cat.name}</span>
                    <p className="text-xs text-foreground-500 truncate">{cat.description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category Grid with Images */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 hover:shadow-sm transition-all duration-200 cursor-pointer"
            >
              <div className="relative h-28 overflow-hidden">
                <img
                  src={cat.imageUrl}
                  alt={cat.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent"></div>
              </div>
              <div className="p-3 text-center">
                <span className="text-xs font-medium text-foreground-700 group-hover:text-foreground-900 leading-tight">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}